import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import type { AutoSaveStatus } from '../../hooks/useAutoSave'
import ConfirmationDialog from '../ConfirmationDialog'
import Modal from '../Modal'
import ProjectHeader from '../ProjectHeader'
import ProjectModalFooter from '../ProjectModalFooter'

type FooterVariant = 'standard' | 'tag'

interface DashboardAddModalProps {
  onClose: () => void
  onSubmit: () => Promise<void>
  onNext?: () => void
  onBack?: () => void
  title: string
  projectId?: string
  poNumber?: string
  step: number
  totalSteps: number
  readOnly?: boolean
  isSubmitting?: boolean
  isNextDisabled?: boolean
  children: React.ReactNode
  onSaveDraft?: () => void
  showSaveDraft?: boolean
  isSavingDraft?: boolean
  onClearDraft?: () => void
  validationError?: string | null
  submitError?: string | null
  onDismissSubmitError?: () => void
  skipCancelConfirmation?: boolean
  skipSendConfirmation?: boolean
  autoSaveStatus?: AutoSaveStatus
  autoSaveLastSaved?: Date | null
  onTrySubmitWithErrors?: () => void
  // Configurable confirmation dialog text
  sendConfirmTitle?: string
  sendConfirmMessage?: string
  sendConfirmLabel?: string
  sendCancelLabel?: string
  cancelConfirmTitle?: string
  cancelConfirmMessage?: string
  cancelConfirmLabel?: string
  cancelCancelLabel?: string
  /** Button styling variant - 'standard' for Project Terms/Dispatches, 'tag' for Tags */
  footerVariant?: FooterVariant
}

const DashboardAddModal: React.FC<DashboardAddModalProps> = ({
  onClose,
  onSubmit,
  onNext,
  onBack,
  title,
  projectId,
  poNumber,
  step,
  totalSteps,
  readOnly = false,
  isSubmitting = false,
  isNextDisabled = false,
  children,
  onSaveDraft,
  showSaveDraft = false,
  isSavingDraft = false,
  onClearDraft,
  validationError,
  submitError,
  onDismissSubmitError,
  skipCancelConfirmation = false,
  skipSendConfirmation = false,
  autoSaveStatus,
  autoSaveLastSaved,
  onTrySubmitWithErrors,
  // Configurable confirmation dialog text with defaults
  sendConfirmTitle = 'Send Project Terms',
  sendConfirmMessage = 'Are you certain you want to send these Project Terms? Once sent, these Project Terms cannot be called back or modified.',
  sendConfirmLabel = 'Yes, send',
  sendCancelLabel = 'Back, I made an error',
  cancelConfirmTitle = 'Delete Project Terms',
  cancelConfirmMessage = 'Are you certain you want to permanently delete these Project Terms? They will no longer be available or accessible to you.',
  cancelConfirmLabel = 'Yes, delete',
  cancelCancelLabel = 'Back, I made an error',
  footerVariant = 'standard',
}) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [showSendConfirm, setShowSendConfirm] = useState(false)

  // Scroll to top when step changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: step is intentionally used as a trigger
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0
    }
  }, [step])

  const handleNext = () => {
    if (step === totalSteps) {
      if (skipSendConfirmation) {
        // Skip confirmation and submit directly
        onSubmit()
      } else {
        // Show send confirmation on final step
        setShowSendConfirm(true)
      }
    } else if (onNext) {
      onNext()
    }
  }

  const handleConfirmSend = () => {
    setShowSendConfirm(false)
    onSubmit()
  }

  const handleBack = () => {
    if (onBack) {
      onBack()
    } else if (skipCancelConfirmation) {
      onClose()
    } else {
      // Show cancel confirmation on first step
      setShowCancelConfirm(true)
    }
  }

  const handleConfirmCancel = () => {
    setShowCancelConfirm(false)
    onClearDraft?.()
    onClose()
  }

  // Format date as "January 25, 2026, 21:28 ET"
  const formatDateTime = () => {
    const now = new Date()
    const dateOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }
    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }
    const dateStr = now.toLocaleDateString('en-US', dateOptions)
    const timeStr = now.toLocaleTimeString('en-US', timeOptions)

    // Get timezone abbreviation
    const tzAbbr = now
      .toLocaleTimeString('en-US', { timeZoneName: 'short' })
      .split(' ')
      .pop()

    return `${dateStr}, ${timeStr} ${tzAbbr}`
  }

  return (
    <Modal onClose={onClose}>
      <div className={css(styles.modal)}>
        <ProjectHeader
          title={readOnly ? `View ${title}` : title}
          date={formatDateTime()}
          projectId={projectId}
          poNumber={poNumber}
          step={step}
          totalSteps={totalSteps}
          autoSaveStatus={autoSaveStatus}
          autoSaveLastSaved={autoSaveLastSaved}
        />

        <div ref={contentRef} className={css(styles.content)}>
          {children}
        </div>

        <ProjectModalFooter
          onCancel={() =>
            skipCancelConfirmation ? onClose() : setShowCancelConfirm(true)
          }
          onNext={handleNext}
          onBack={handleBack}
          isFirstStep={step === 1}
          nextLabel={readOnly ? 'Close' : step === totalSteps ? 'Send' : 'Next'}
          isLoading={isSubmitting && step === totalSteps}
          showSubmit={!readOnly && step === totalSteps}
          isNextDisabled={isNextDisabled}
          onSaveDraft={onSaveDraft}
          showSaveDraft={showSaveDraft && !readOnly}
          isSavingDraft={isSavingDraft}
          validationError={validationError}
          submitError={submitError}
          onDismissSubmitError={onDismissSubmitError}
          onTrySubmitWithErrors={onTrySubmitWithErrors}
          onClose={onClose}
          showCloseButton={!readOnly}
          variant={footerVariant}
        />
      </div>

      {/* Cancel Confirmation Dialog */}
      {showCancelConfirm && (
        <ConfirmationDialog
          title={cancelConfirmTitle}
          message={cancelConfirmMessage}
          confirmLabel={cancelConfirmLabel}
          cancelLabel={cancelCancelLabel}
          onConfirm={handleConfirmCancel}
          onCancel={() => setShowCancelConfirm(false)}
          isDestructive={true}
          reverseButtons={true}
        />
      )}

      {/* Send Confirmation Dialog */}
      {showSendConfirm && (
        <ConfirmationDialog
          title={sendConfirmTitle}
          message={sendConfirmMessage}
          confirmLabel={sendConfirmLabel}
          cancelLabel={sendCancelLabel}
          onConfirm={handleConfirmSend}
          onCancel={() => setShowSendConfirm(false)}
          isLoading={isSubmitting}
          reverseButtons={true}
        />
      )}
    </Modal>
  )
}

const styles = StyleSheet.create({
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '900px',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90vh',
    '@media (max-width: 960px)': {
      maxWidth: '95vw',
    },
  },
  content: {
    padding: '24px',
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
  },
})

export default DashboardAddModal
